import React from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useI18n } from "@/contexts/I18nContext";

export default function Cookies() {
  const { t, locale } = useI18n();
  const body = COOKIE_TEXT[locale] || COOKIE_TEXT.en;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
        <Link to="/" className="text-xs font-bold uppercase tracking-[0.2em] text-[#7AAA20] hover:text-[#9ACD32]">
          {t("legal.back_home")}
        </Link>
        <h1 className="mt-4 font-display text-3xl font-extrabold text-[#0B0E5C] sm:text-4xl lg:text-5xl">
          {body.title}
        </h1>
        <p className="mt-3 text-sm text-slate-500">{body.last_updated}</p>

        <div className="prose prose-slate mt-8 max-w-none text-slate-600">
          {body.sections.map((s, i) => (
            <section key={i} className="mb-7">
              <h2 className="font-display text-xl font-bold text-[#0B0E5C]">{s.h}</h2>
              <p className="mt-2 text-[15px] leading-relaxed">{s.p}</p>
            </section>
          ))}

          <section className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm">
            <p>{body.contact}</p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const COOKIE_TEXT = {
  en: {
    title: "Cookie Policy",
    last_updated: "Last updated: February 2026",
    sections: [
      { h: "What are cookies?", p: "Cookies are small text files stored by your browser when you visit a website. NEURACOM also uses a similar technology — localStorage — to remember your preferences and keep you signed in." },
      { h: "Strictly necessary (always active)", p: "Authentication token (HttpOnly cookie + localStorage), CSRF protection, your preferred language. These cannot be disabled — without them the platform cannot work." },
      { h: "Functional (enabled by default, can be cleared)", p: "Your interface preferences (language, recently used model), cookie-consent record (so we don't ask again), referral source tracking when you arrive through a partner link." },
      { h: "Analytics & marketing", p: "We do NOT currently use third-party analytics, fingerprinting, advertising pixels or behavioural tracking. If we ever add them they will be opt-in through the consent banner and disabled by default." },
      { h: "Third-party services", p: "Stripe (payments) may set cookies when you reach the checkout page. AI providers (Anthropic, OpenAI, Google) process your prompts but do not set cookies on this domain." },
      { h: "Manage your choices", p: "You can review and change your cookie preferences any time from Settings → Cookie preferences inside your account, or by clearing your browser storage for this site." },
    ],
    contact: "Questions? Email privacy@telcavoip.eu — Data controller: TelcaVoIP International EU (VAT PL8513242072).",
  },
  it: {
    title: "Cookie Policy",
    last_updated: "Ultimo aggiornamento: Febbraio 2026",
    sections: [
      { h: "Cosa sono i cookie?", p: "I cookie sono piccoli file di testo memorizzati dal browser quando visiti un sito. NEURACOM usa anche una tecnologia simile — localStorage — per ricordare le tue preferenze e tenerti collegato." },
      { h: "Tecnici (sempre attivi)", p: "Token di autenticazione (cookie HttpOnly + localStorage), protezione CSRF, lingua preferita. Non possono essere disattivati — senza di essi la piattaforma non funziona." },
      { h: "Funzionali (attivi di default, eliminabili)", p: "Preferenze di interfaccia (lingua, modello usato di recente), registro del consenso cookie (per non chiederlo ogni volta), tracciamento del partner di provenienza se atterri da un link referenziato." },
      { h: "Analitica e marketing", p: "Non utilizziamo attualmente analytics di terze parti, fingerprinting, pixel pubblicitari o tracciamento comportamentale. Se li introdurremo saranno opt-in tramite il banner di consenso e disattivati di default." },
      { h: "Servizi di terze parti", p: "Stripe (pagamenti) può impostare cookie quando raggiungi il checkout. I provider AI (Anthropic, OpenAI, Google) elaborano i tuoi prompt ma non impostano cookie sul nostro dominio." },
      { h: "Gestisci le tue scelte", p: "Puoi rivedere e modificare le preferenze cookie in qualsiasi momento da Impostazioni → Preferenze cookie nel tuo account, oppure cancellando lo storage del browser per questo sito." },
    ],
    contact: "Domande? Scrivi a privacy@telcavoip.eu — Titolare del trattamento: TelcaVoIP International EU (P.IVA PL8513242072).",
  },
  pl: {
    title: "Polityka Cookie",
    last_updated: "Ostatnia aktualizacja: Luty 2026",
    sections: [
      { h: "Czym są pliki cookie?", p: "Pliki cookie to małe pliki tekstowe przechowywane przez przeglądarkę podczas odwiedzania strony. NEURACOM używa też podobnej technologii — localStorage — aby zapamiętać Twoje preferencje i utrzymać sesję." },
      { h: "Niezbędne (zawsze aktywne)", p: "Token uwierzytelniający (cookie HttpOnly + localStorage), ochrona CSRF, preferowany język. Tych nie można wyłączyć — bez nich platforma nie działa." },
      { h: "Funkcjonalne (domyślnie włączone, można wyczyścić)", p: "Preferencje interfejsu (język, ostatnio używany model), zapis zgody cookie (abyśmy nie pytali ponownie), śledzenie partnera, jeśli przyszedłeś z linku afiliacyjnego." },
      { h: "Analityka i marketing", p: "Obecnie NIE używamy analityki firm trzecich, fingerprintingu, pikseli reklamowych ani śledzenia behawioralnego. Jeśli kiedykolwiek je dodamy, będą opt-in przez banner zgody i domyślnie wyłączone." },
      { h: "Usługi firm trzecich", p: "Stripe (płatności) może ustawiać cookies przy stronie checkout. Dostawcy AI (Anthropic, OpenAI, Google) przetwarzają Twoje prompty, ale nie ustawiają cookies w tej domenie." },
      { h: "Zarządzaj wyborami", p: "Możesz przejrzeć i zmienić preferencje cookie w dowolnym momencie w Ustawienia → Preferencje cookie w swoim koncie, lub czyszcząc storage przeglądarki dla tej witryny." },
    ],
    contact: "Pytania? Napisz na privacy@telcavoip.eu — Administrator danych: TelcaVoIP International EU (NIP UE PL8513242072).",
  },
};
